'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildTimeAxis, CLOSED_RANGE_KEYS, ClosedRange } from '@/utils/coins/marketClosed';
import { useTweenedPoints } from '@/utils/coins/useTweenedPoints';

/**
 * Price chart drawn in plain SVG (no chart library), the same design as the TetherChand app:
 * - line is green above the range's opening price and red below it (dashed baseline)
 * - price axis with grid lines and a last-price tag
 * - time axis whose labels follow the selected range (hours → weekday → day → month → year)
 * - hover (mouse) or press-and-drag (finger): crosshair, a floating info card and the exact time on the time axis
 * - market-closed hours (from the API) squeezed into narrow «بسته» bands on short ranges
 * - newer data slides onto the end of the line; a pulsing dot marks the live price
 */

export interface ChartPoint {
    timestamp: number; // seconds
    value: number;
    /** Extra rows for the info card, e.g. [{ label: 'قیمت تومانی', value: '۱۲۳ تومان' }] */
    extra?: { label: string; value: string }[];
}

interface Props {
    points: ChartPoint[];
    /** Selected range key (1h, 3h, 12h, 24h, 7d, 30d, 3m, 1y, 3y, 5y) */
    range: string;
    /** Price text without unit */
    formatValue: (v: number) => string;
    /** Unit after prices in the info card, e.g. «$» or «تومان» */
    unit?: string;
    height?: number;
    /** Label of the price row in the info card */
    valueLabel?: string;
    /** Market-closed hours to squeeze out of the time axis (short ranges only) */
    closedRanges?: ClosedRange[];
    /** The last point is a live price: mark it with a pulsing dot */
    live?: boolean;
    /** Same key as the previous render = same chart with newer data → animate the change; a new key (or
     *  none) draws the new data at once */
    animateKey?: string;
}

const GREEN = '#16C784';
const RED = '#EA3943';
const AXIS_H = 26; // time axis height
const CARD_W = 210;
const CHAR_W = 6; // rough width of one axis character at 10px

const toFa = (s: string | number) => String(s).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[+d]);

const fmt = (ts: number, opts: Intl.DateTimeFormatOptions) => {
    try {
        return new Date(ts * 1000).toLocaleString('fa-IR', opts);
    } catch {
        return '';
    }
};

/** Seconds to add to UTC to get the viewer's wall clock (Iran has no DST) */
const tzOffset = (ts: number) => -new Date(ts * 1000).getTimezoneOffset() * 60;

let persianFmt: Intl.DateTimeFormat | null = null;
/** Solar Hijri year / month / day of a moment */
function persianDate(ts: number) {
    persianFmt ??= new Intl.DateTimeFormat('en-US-u-ca-persian', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });
    const o: Record<string, string> = {};
    for (const p of persianFmt.formatToParts(new Date(ts * 1000))) o[p.type] = p.value;
    return { y: parseInt(o.year, 10), m: parseInt(o.month, 10), d: parseInt(o.day, 10) };
}

// Time-axis steps, like CoinMarketCap: round clock times, whole days, weeks, then calendar months / years
const CLOCK_STEPS = [300, 600, 900, 1800, 3600, 7200, 10800, 21600, 43200];
const DAY_STEPS = [1, 2, 7, 14].map((d) => d * 86400);
const MONTH_STEPS = [1, 2, 3, 6, 12, 24, 60];

interface TimeTick {
    t: number;
    label: string;
}

/** Ticks on round times between t0 and t1, at most `max` of them */
function timeTicks(t0: number, t1: number, max: number): TimeTick[] {
    const span = t1 - t0;
    const off = tzOffset(t1);
    const dayLabel = (t: number) => fmt(t, { day: 'numeric', month: 'short' });

    const clock = CLOCK_STEPS.find((s) => span / s <= max);
    if (clock) {
        const out: TimeTick[] = [];
        for (let t = Math.ceil((t0 + off) / clock) * clock - off; t <= t1; t += clock) {
            // A new day shows its date instead of 00:00
            const midnight = (t + off) % 86400 === 0;
            out.push({
                t,
                label: midnight
                    ? dayLabel(t)
                    : fmt(t, { hour: '2-digit', minute: '2-digit', hour12: false }),
            });
        }
        return out;
    }

    const firstMidnight = Math.ceil((t0 + off) / 86400) * 86400 - off;
    const days = DAY_STEPS.find((s) => span / s <= max);
    if (days) {
        const out: TimeTick[] = [];
        for (let t = firstMidnight; t <= t1; t += days) out.push({ t, label: dayLabel(t) });
        return out;
    }

    // Months: first day of a Solar Hijri month (every n-th month), the year at Nowruz
    const months = MONTH_STEPS.find((m) => span / (m * 30.44 * 86400) <= max) || 60;
    const out: TimeTick[] = [];
    for (let t = firstMidnight; t <= t1; t += 86400) {
        const p = persianDate(t + 3600);
        if (p.d !== 1 || (p.y * 12 + p.m - 1) % months !== 0) continue;
        out.push({
            t,
            label:
                p.m === 1 || months >= 12
                    ? fmt(t, { year: 'numeric' })
                    : fmt(t, { month: 'short' }),
        });
    }
    return out;
}

/** «Nice» price-axis steps: 1, 2, 2.5, 5 × 10^n */
function niceStep(span: number, count: number): number {
    const raw = span / count;
    const mag = Math.pow(10, Math.floor(Math.log10(raw)));
    return [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || 10 * mag;
}

/**
 * Price-axis number format: one unit (K / M / B / T) for the whole axis and just enough decimals for the step,
 * so neighbouring labels never read the same (80.4K · 80.6K, 1.325 · 1.330, 0.00001234).
 */
function priceFormat(maxAbs: number, step: number) {
    const [div, suffix] =
        maxAbs >= 1e12
            ? [1e12, 'T']
            : maxAbs >= 1e9
              ? [1e9, 'B']
              : maxAbs >= 1e6
                ? [1e6, 'M']
                : maxAbs >= 1e4
                  ? [1e3, 'K']
                  : [1, ''];
    const s = step / div;
    // Enough decimals to write the step exactly (0.25 → 2, 0.00000002 → 8)
    let decimals = Math.max(0, Math.ceil(-Math.log10(s) - 1e-9));
    while (decimals < 12 && Math.abs(s * 10 ** decimals - Math.round(s * 10 ** decimals)) > 1e-6)
        decimals++;
    const write = (v: number, d: number) =>
        (v / div || 0).toLocaleString('en-US', {
            minimumFractionDigits: d,
            maximumFractionDigits: d,
        }) + suffix;
    return {
        tick: (v: number) => write(v, decimals),
        /** Last-price tag: at least 4 significant digits (80.52K · 268.0M · 0.00001020) */
        price: (v: number) => {
            const a = Math.abs(v / div);
            const sig = a > 0 ? Math.max(0, 4 - (Math.floor(Math.log10(a)) + 1)) : 0;
            return write(v, Math.min(12, Math.max(decimals, sig)));
        },
    };
}

/** Full time under the crosshair / at the top of the info card */
function fullLabel(ts: number, range: string): string {
    if (['3m', '1y', '3y', '5y'].includes(range)) {
        return fmt(ts, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    }
    return fmt(ts, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}

const PriceAreaChart: React.FC<Props> = ({
    points: input,
    range,
    formatValue,
    unit = '',
    height = 260,
    valueLabel = 'قیمت',
    closedRanges,
    live = false,
    animateKey,
}) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    const [hover, setHover] = useState<number | null>(null);
    const uid = React.useId().replace(/:/g, '');

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const update = () => setWidth(el.getBoundingClientRect().width);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const plotH = height - AXIS_H;

    const sorted = useMemo(
        () => input.filter((p) => isFinite(p.value)).sort((a, b) => a.timestamp - b.timestamp),
        [input],
    );
    const ownKey = useRef({ input: sorted, n: 0 });
    if (ownKey.current.input !== sorted)
        ownKey.current = { input: sorted, n: ownKey.current.n + 1 };
    const shown = useTweenedPoints(sorted, animateKey ?? `own-${ownKey.current.n}`);
    const squeeze =
        closedRanges?.length && CLOSED_RANGE_KEYS.includes(range) ? closedRanges : undefined;

    const model = useMemo(() => {
        if (shown.length < 2) return null;
        // The window always spans all points; prices inside closed hours aren't drawn (nothing traded),
        // the line joins the last price before the closure to the first one after it
        const t0 = shown[0].timestamp;
        const t1 = shown[shown.length - 1].timestamp;
        const closedAt = buildTimeAxis(t0, t1, 1000, squeeze);
        const open = closedAt.gaps.length
            ? shown.filter((p) => !closedAt.inGap(p.timestamp))
            : shown;
        const pts = open.length >= 2 ? open : shown;
        if (pts.length < 2 || width <= 0) return null;
        const values = pts.map((p) => p.value);
        let lo = Math.min(...values);
        let hi = Math.max(...values);
        const pad = (hi - lo) * 0.08 || Math.abs(hi) * 0.01 || 1;
        // Prices never go below zero, so neither does the axis
        lo = lo > 0 ? Math.max(0, lo - pad) : lo - pad;
        hi += pad;

        // Price axis: 4–7 round levels depending on the height
        const step = niceStep(hi - lo, Math.max(3, Math.min(6, Math.floor(plotH / 50))));
        const yValues: number[] = [];
        for (let v = Math.ceil(lo / step) * step; v <= hi + step * 1e-9; v += step) yValues.push(v);
        const fmtPrice = priceFormat(Math.max(Math.abs(lo), Math.abs(hi)), step);
        const lastLabel = fmtPrice.price(pts[pts.length - 1].value);
        const yLabels = yValues.map((v) => fmtPrice.tick(v));
        const longest = Math.max(lastLabel.length, ...yLabels.map((l) => l.length));
        const axisW = Math.max(44, Math.ceil(longest * CHAR_W + 14));
        const plotW = Math.max(0, width - axisW);

        const axis = buildTimeAxis(t0, t1, plotW, open.length >= 2 ? squeeze : undefined);
        const x = axis.x;
        const y = (v: number) => plotH - ((v - lo) / (hi - lo)) * plotH;
        const xy = pts.map((p) => ({ ...p, x: x(p.timestamp), y: y(p.value) }));
        const line = xy
            .map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
            .join('');
        const base = pts[0].value;
        const baseY = y(base);
        const yTicks = yValues
            .map((v, i) => ({ v, y: y(v), label: yLabels[i] }))
            .filter((t) => t.y > 6 && t.y < plotH - 6);

        // Time axis: round times; a label that would stick out of the plot is left out (never shifted)
        // (and none inside or overlapping a closed band)
        let lastRight = -Infinity;
        const xTicks = timeTicks(t0, t1, Math.max(2, Math.floor(plotW / 95)))
            .filter((tk) => !axis.inGap(tk.t))
            .map((tk) => ({ ...tk, label: toFa(tk.label), x: x(tk.t) }))
            .filter((tk) => {
                const half = (tk.label.length * CHAR_W) / 2;
                const ok =
                    tk.x - half >= 0 &&
                    tk.x + half <= plotW &&
                    tk.x - half > lastRight + 6 &&
                    !axis.gaps.some((g) => tk.x + half > g.x0 && tk.x - half < g.x1);
                if (ok) lastRight = tk.x + half;
                return ok;
            });

        return {
            xy,
            line,
            base,
            baseY,
            yTicks,
            xTicks,
            plotW,
            axisW,
            lastLabel,
            gaps: axis.gaps,
        };
    }, [shown, width, plotH, squeeze]);

    const plotW = model?.plotW ?? 0;
    const axisW = model?.axisW ?? 0;

    // Another range / size → drop the old crosshair (new live points keep it)
    useEffect(() => setHover(null), [range, width]);

    const pick = useCallback(
        (clientX: number) => {
            if (!model || !wrapRef.current) return;
            const rect = wrapRef.current.getBoundingClientRect();
            const px = Math.max(0, Math.min(plotW, clientX - rect.left));
            const pts = model.xy;
            let a = 0;
            let b = pts.length - 1;
            while (b - a > 1) {
                const m = (a + b) >> 1;
                if (pts[m].x < px) a = m;
                else b = m;
            }
            setHover(px - pts[a].x <= pts[b].x - px ? a : b);
        },
        [model, plotW],
    );

    // Finger: touch-action pan-y keeps vertical swipes scrolling the page (that cancels the pointer and hides
    // the card); a horizontal drag scrubs along the line. Lifting the finger hides the card.
    const onPointerDown = (e: React.PointerEvent) => pick(e.clientX);
    const onPointerMove = (e: React.PointerEvent) => {
        if (e.pointerType === 'mouse' || e.buttons) pick(e.clientX);
    };
    const endTouch = (e: React.PointerEvent) => {
        if (e.pointerType !== 'mouse') setHover(null);
    };

    const hp = model && hover !== null ? model.xy[Math.min(hover, model.xy.length - 1)] : null;
    const last = model ? model.xy[model.xy.length - 1] : null;
    const lastUp = model && last ? last.value >= model.base : true;

    // Info card beside the point, flipped near the edges
    const cardLeft = hp
        ? hp.x + 14 + CARD_W > plotW
            ? Math.max(0, hp.x - 14 - CARD_W)
            : hp.x + 14
        : 0;
    const hpChange = hp && model ? ((hp.value - model.base) / Math.abs(model.base || 1)) * 100 : 0;

    return (
        <div
            ref={wrapRef}
            className='relative w-full select-none'
            style={{ height, touchAction: 'pan-y' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endTouch}
            onPointerCancel={endTouch}
            onPointerLeave={(e) => {
                if (e.pointerType === 'mouse') setHover(null);
            }}
            dir='ltr'
        >
            {model && (
                <svg width={width} height={height} className='block overflow-visible'>
                    <defs>
                        {/* Above the opening price = green, below = red */}
                        <clipPath id={`${uid}-up`}>
                            <rect x={0} y={-10} width={plotW} height={model.baseY + 10} />
                        </clipPath>
                        <clipPath id={`${uid}-dn`}>
                            <rect
                                x={0}
                                y={model.baseY}
                                width={plotW}
                                height={plotH - model.baseY + 10}
                            />
                        </clipPath>
                        {/* Strongest at the far edge of each area, fading towards the opening price */}
                        <linearGradient
                            id={`${uid}-gu`}
                            gradientUnits='userSpaceOnUse'
                            x1='0'
                            y1='0'
                            x2='0'
                            y2={model.baseY}
                        >
                            <stop offset='0%' stopColor={GREEN} stopOpacity={0.3} />
                            <stop offset='100%' stopColor={GREEN} stopOpacity={0.03} />
                        </linearGradient>
                        <linearGradient
                            id={`${uid}-gd`}
                            gradientUnits='userSpaceOnUse'
                            x1='0'
                            y1={plotH}
                            x2='0'
                            y2={model.baseY}
                        >
                            <stop offset='0%' stopColor={RED} stopOpacity={0.3} />
                            <stop offset='100%' stopColor={RED} stopOpacity={0.03} />
                        </linearGradient>
                    </defs>

                    {/* Grid + price axis */}
                    {model.yTicks.map((t) => (
                        <g key={t.v}>
                            <line
                                x1={0}
                                x2={plotW}
                                y1={t.y}
                                y2={t.y}
                                className='stroke-slate-200 dark:stroke-white/[0.06]'
                                strokeWidth={1}
                            />
                            <text
                                x={plotW + 6}
                                y={t.y + 3.5}
                                fontSize={10}
                                className='fill-slate-400 dark:fill-slate-500'
                                style={{ fontFamily: 'inherit' }}
                            >
                                {t.label}
                            </text>
                        </g>
                    ))}

                    {/* Market closed: narrow hatched bands */}
                    {model.gaps.map((g) => (
                        <g key={g.from}>
                            <title>بازار بسته (توقف معاملات)</title>
                            <rect
                                x={g.x0}
                                y={0}
                                width={Math.max(1, g.x1 - g.x0)}
                                height={plotH}
                                className='fill-slate-200/70 dark:fill-white/[0.06]'
                            />
                            <line
                                x1={g.x0}
                                x2={g.x0}
                                y1={0}
                                y2={plotH}
                                className='stroke-slate-300 dark:stroke-white/15'
                                strokeDasharray='3 3'
                            />
                            <line
                                x1={g.x1}
                                x2={g.x1}
                                y1={0}
                                y2={plotH}
                                className='stroke-slate-300 dark:stroke-white/15'
                                strokeDasharray='3 3'
                            />
                            <text
                                transform={`translate(${(g.x0 + g.x1) / 2 + 3.5},${plotH / 2}) rotate(-90)`}
                                fontSize={9}
                                textAnchor='middle'
                                className='fill-slate-400 dark:fill-slate-500'
                                style={{ fontFamily: 'inherit' }}
                            >
                                بسته
                            </text>
                        </g>
                    ))}

                    {/* Areas between the line and the opening price */}
                    <path
                        d={`${model.line}L${plotW},${model.baseY}L0,${model.baseY}Z`}
                        fill={`url(#${uid}-gu)`}
                        clipPath={`url(#${uid}-up)`}
                    />
                    <path
                        d={`${model.line}L${plotW},${model.baseY}L0,${model.baseY}Z`}
                        fill={`url(#${uid}-gd)`}
                        clipPath={`url(#${uid}-dn)`}
                    />
                    {/* Opening price */}
                    <line
                        x1={0}
                        x2={plotW}
                        y1={model.baseY}
                        y2={model.baseY}
                        className='stroke-slate-400/60 dark:stroke-slate-500/60'
                        strokeDasharray='2 3'
                        strokeWidth={1}
                    />
                    {/* Line, two colours */}
                    <path
                        d={model.line}
                        fill='none'
                        stroke={GREEN}
                        strokeWidth={2}
                        strokeLinejoin='round'
                        clipPath={`url(#${uid}-up)`}
                    />
                    <path
                        d={model.line}
                        fill='none'
                        stroke={RED}
                        strokeWidth={2}
                        strokeLinejoin='round'
                        clipPath={`url(#${uid}-dn)`}
                    />

                    {/* Last price tag on the axis */}
                    {last && (
                        <g>
                            <line
                                x1={last.x}
                                x2={plotW}
                                y1={last.y}
                                y2={last.y}
                                stroke={lastUp ? GREEN : RED}
                                strokeDasharray='1 3'
                                strokeWidth={1}
                                opacity={0.7}
                            />
                            <rect
                                x={plotW + 2}
                                y={last.y - 9}
                                width={axisW - 2}
                                height={18}
                                rx={4}
                                fill={lastUp ? GREEN : RED}
                            />
                            <text
                                x={plotW + 6}
                                y={last.y + 3.5}
                                fontSize={10}
                                fontWeight={700}
                                fill='#fff'
                                style={{ fontFamily: 'inherit' }}
                            >
                                {model.lastLabel}
                            </text>
                            {live && (
                                <circle cx={last.x} cy={last.y} r={3.5} fill={lastUp ? GREEN : RED}>
                                    <animate
                                        attributeName='r'
                                        values='3.5;8;3.5'
                                        dur='1.8s'
                                        repeatCount='indefinite'
                                    />
                                    <animate
                                        attributeName='opacity'
                                        values='1;0.25;1'
                                        dur='1.8s'
                                        repeatCount='indefinite'
                                    />
                                </circle>
                            )}
                        </g>
                    )}

                    {/* Time axis */}
                    {model.xTicks.map((t, i) => (
                        <text
                            key={i}
                            // Persian text: RTL so «۷ آبان» doesn't read as «آبان ۷» inside the LTR chart
                            direction='rtl'
                            x={t.x}
                            y={plotH + 17}
                            fontSize={10}
                            textAnchor='middle'
                            className='fill-slate-400 dark:fill-slate-500'
                            style={{ fontFamily: 'inherit', opacity: hp ? 0.35 : 1 }}
                        >
                            {t.label}
                        </text>
                    ))}

                    {/* Crosshair */}
                    {hp && (
                        <g>
                            <line
                                x1={hp.x}
                                x2={hp.x}
                                y1={0}
                                y2={plotH}
                                className='stroke-slate-400 dark:stroke-slate-500'
                                strokeDasharray='3 3'
                                strokeWidth={1}
                            />
                            <line
                                x1={0}
                                x2={plotW}
                                y1={hp.y}
                                y2={hp.y}
                                className='stroke-slate-400/50 dark:stroke-slate-500/50'
                                strokeDasharray='3 3'
                                strokeWidth={1}
                            />
                            <circle
                                cx={hp.x}
                                cy={hp.y}
                                r={5}
                                fill={hp.value >= model.base ? GREEN : RED}
                                stroke='#fff'
                                strokeWidth={2}
                            />
                        </g>
                    )}
                </svg>
            )}

            {/* Exact time under the crosshair, on the time axis */}
            {hp && (
                <div
                    className='absolute pointer-events-none whitespace-nowrap rounded-md bg-slate-700 dark:bg-slate-600 text-white text-[10px] font-bold px-2 py-0.5'
                    style={{
                        top: plotH + 4,
                        left: Math.max(0, Math.min(plotW - 170, hp.x - 85)),
                        width: 170,
                        textAlign: 'center',
                    }}
                    dir='rtl'
                >
                    {toFa(fullLabel(hp.timestamp, range))}
                </div>
            )}

            {/* Info card */}
            {hp && model && (
                <div
                    className='absolute pointer-events-none z-10 rounded-xl border border-slate-200 dark:border-white/10 bg-white/95 dark:bg-slate-900/95 shadow-lg backdrop-blur-sm px-3 py-2.5 text-[11px]'
                    style={{ top: 6, left: cardLeft, width: CARD_W }}
                    dir='rtl'
                >
                    <div className='font-bold text-slate-500 dark:text-slate-400 mb-1.5'>
                        {toFa(fullLabel(hp.timestamp, range))}
                    </div>
                    <div className='flex items-center justify-between gap-2'>
                        <span className='flex items-center gap-1.5 text-slate-500 dark:text-slate-400'>
                            <span
                                className='w-2 h-2 rounded-full'
                                style={{ background: hp.value >= model.base ? GREEN : RED }}
                            />
                            {valueLabel}
                        </span>
                        <span className='font-black text-slate-800 dark:text-slate-100 whitespace-nowrap'>
                            <span dir='ltr'>{formatValue(hp.value)}</span> {unit}
                        </span>
                    </div>
                    <div className='flex items-center justify-between gap-2 mt-1'>
                        <span className='text-slate-500 dark:text-slate-400'>
                            تغییر از ابتدای بازه
                        </span>
                        <span
                            className='font-bold'
                            style={{ color: hpChange >= 0 ? GREEN : RED }}
                            dir='ltr'
                        >
                            {hpChange > 0 ? '+' : hpChange < 0 ? '-' : ''}
                            {Math.abs(hpChange).toFixed(2)}%
                        </span>
                    </div>
                    {hp.extra?.map((row) => (
                        <div
                            key={row.label}
                            className='flex items-center justify-between gap-2 mt-1'
                        >
                            <span className='text-slate-500 dark:text-slate-400'>{row.label}</span>
                            <span className='font-bold text-slate-700 dark:text-slate-200 whitespace-nowrap'>
                                {row.value}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PriceAreaChart;
